import React, { memo } from "react";

import { Divider, Grid } from "@material-ui/core";
import { useField, useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import styled from "styled-components/macro";

import { requeryABNForBusiness } from "actions";

import GlobalButton from "components/Button";
import { GSTField } from "components/Form/Fields";
import {
  CheckBox,
  ControlledLifeCycleInput,
  Input,
  OptionTogglerField,
  withNamespace,
} from "components/Form/FormikControls";
import { Row } from "components/Layout";
import { WarningIcon } from "components/ScanningScreen/ScanDiscrepancyDialog";

import { GSTOverrideOptions } from "constants/billing";
import { AbnStatus } from "constants/businesses";

import { getSaleyardName } from "lib/navigation";
import { formatDateTimeUTC } from "lib/timeFormats";

import { useHasGSTOverridePermission } from "hooks";

const AbrLastCheckedText = styled.p(
  ({ theme }) => `
        font-size: ${theme.fontSizes.alpha}px;
        margin-left: ${theme.space[2]}px;  
    `,
);

 
function parseACNValue(value) {
  if (value === null) {
    return null;
  }
  return value.replaceAll(/[^\d]/g, "").slice(0, 9);
}

const AbrDetailsFormComponent = ({
  namespace: ns,
  businessId,
  abn,
  abrLastChecked,
  showBpay,
}) => {
  const dispatch = useDispatch();

  const formikProps = useFormikContext();
  const { dirty, initialValues } = formikProps;
  const { abn: initialAbn } = initialValues;
  const isEdit = !!businessId;
  const hasAbn = !!abn;
  // template string comparison to handle mixed types
  const abnDirty = dirty && `${initialAbn}` !== `${abn}`;
  const allowAbrQuery = hasAbn && !abnDirty;

  const requeryAbr = () => {
    dispatch(requeryABNForBusiness(businessId, getSaleyardName()));
  };

  const hasGSTOverridePermission = useHasGSTOverridePermission();

  const [{ value: isABNActive }] = useField(withNamespace(ns, "isABNActive"));

  return (
    <>
      <Grid item xs={6}>
        <Input
          label="ABN"
          name={withNamespace(ns, "abn")}
          type="number"
          tooltip="Adding an ABN will query the ABR for the GST Status, ACN and Trading Name"
          emptyValue={null}
        />
        {isABNActive === false && (
          <p>
            <WarningIcon size="1x" /> {AbnStatus.CANCELLED}
          </p>
        )}
      </Grid>
      <Grid item xs={6}>
        <ControlledLifeCycleInput
          label="ACN"
          name={withNamespace(ns, "acn")}
          disabled={hasAbn}
          parseInputValue={parseACNValue}
          tooltip={hasAbn ? "Populated after querying the ABR." : null}
        />
      </Grid>
      <Grid item xs={8}>
        <Input
          label="Trading Name"
          name={withNamespace(ns, "tradingName")}
          disabled
          tooltip="Populated after adding an ABN and querying the ABR."
        />
      </Grid>
      <Grid item container xs={4}>
        <GSTField
          name={withNamespace(ns, "isGSTRegistered")}
          isABNActive={isABNActive}
          abrLastChecked={abrLastChecked}
        />
      </Grid>
      {isEdit && (
        <Grid item xs={12}>
          <Row fullWidth justifyBetween>
            <GlobalButton
              onClick={requeryAbr}
              disabled={!allowAbrQuery}
              padding={1}
              data-tour="abr-query-button"
            >
              Query ABR
            </GlobalButton>
            <AbrLastCheckedText>
              {abrLastChecked &&
                `Last Checked: ${formatDateTimeUTC(abrLastChecked)}`}
            </AbrLastCheckedText>
            {hasGSTOverridePermission ? (
              <Row>
                <OptionTogglerField
                  name="shouldChargeGSTOverride"
                  label="Manual GST Override"
                  options={GSTOverrideOptions}
                  tooltip="Allows manual override of the business's GST status.   If no override is in place Hobby Farmer and GST Status derived from the ABN are used."
                  labelPosition="top"
                />
              </Row>
            ) : null}
          </Row>
          {allowAbrQuery && (
            <Row fullWidth>
              <p>
                Query the Australia Business Register (ABR), to populate the GST
                Status, ACN and Trading Name
              </p>
            </Row>
          )}
        </Grid>
      )}
      {showBpay && (
        <Grid item container xs={12} md={12}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Row fullWidth alignEnd gridGap={2}>
            <Grid item xs={12} md={9}>
              <Input
                label="BPay CRN"
                maxLength={20}
                name="bpayCustomerReferenceNumber"
                tooltip="CRN for client to pay agency"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CheckBox
                id="generateBpayCRNCheckBox"
                label="Request new BPay CRN"
                name="generate_bpay_crn"
              />
            </Grid>
          </Row>
        </Grid>
      )}
    </>
  );
};

export default memo(AbrDetailsFormComponent);
